import { StatusDot, Tag } from "@/shared/rd";
import { keys } from "@/lib/i18n/portal";
import { useTranslations } from "@/lib/i18n/intl";
import { sanitizedJson, snapshotRows } from "../services/selection";
import type { SimulatorCheck, SimulatorRun } from "../types/simulator.types";
import { SimCard, Technical } from "./SimulatorParts";
const copy = keys.integrationSimulator;
const meta = { margin: 0, fontSize: 13, color: "var(--rd-text-tertiary)" } as const;
export function CheckList({ checks }: { checks: SimulatorCheck[] }) {
  const t = useTranslations();
  return (
    <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column" }}>
      {checks.map((check, index) => (
        <li
          key={`${check.name}-${index}`}
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            padding: "8px 0",
            borderBottom: "1px solid var(--rd-line-row)",
            fontSize: 13,
          }}
        >
          <span>
            {t(
              (copy.checkNames as Record<string, string>)[check.name] ??
                copy.unknownCode,
              { code: check.name },
            )}
          </span>
          <StatusDot tone={check.passed ? "green" : "red"}>
            {t(check.passed ? copy.checkPassed : copy.checkFailed)}
          </StatusDot>
        </li>
      ))}
    </ul>
  );
}
export function SimulationResult({ result }: { result: SimulatorRun }) {
  const t = useTranslations();
  const cell = (value: unknown) =>
    value === undefined || value === null
      ? t(copy.noValue)
      : typeof value === "object"
        ? sanitizedJson(value)
        : String(value);
  const th = {
    padding: "8px 12px",
    textAlign: "start",
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "var(--rd-text-tertiary)",
    background: "var(--rd-surface-soft)",
    borderBottom: "1px solid var(--rd-line-header)",
  } as const;
  const td = {
    padding: "8px 12px",
    borderBottom: "1px solid var(--rd-line-row)",
    maxWidth: 208,
    overflowWrap: "anywhere",
  } as const;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
        <StatusDot tone={result.passed ? "green" : "red"}>
          {t(result.passed ? copy.checksPassed : copy.checksFailed)}
        </StatusDot>
        <Tag tone="grey">{t(copy.isolated)}</Tag>
      </div>
      <p style={meta}>{t(copy.acceptanceNote)}</p>
      <p style={meta}>{t(copy.executionNote)}</p>
      <p style={meta}>{t(copy.ledgerNote)}</p>
      {result.steps.map((step) => {
        const rows = snapshotRows(step.before, step.after);
        const businessChanged = rows.some(
          (row) =>
            !row.field.startsWith("counts.") &&
            sanitizedJson(row.before) !== sanitizedJson(row.after),
        );
        const auditChanged = rows.some(
          (row) =>
            row.field.startsWith("counts.") &&
            sanitizedJson(row.before) !== sanitizedJson(row.after),
        );
        return (
          <SimCard key={step.sequence} title={t(copy.callback, { number: step.sequence })}>
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 12 }}>
              <StatusDot tone={step.acceptance.accepted ? "green" : "amber"}>
                {t(step.acceptance.accepted ? copy.accepted : copy.rejected)}
              </StatusDot>
              <StatusDot tone={businessChanged ? "blue" : "grey"}>
                {t(businessChanged ? copy.stateChanged : copy.stateUnchanged)}
              </StatusDot>
              <StatusDot tone={auditChanged ? "blue" : "grey"}>
                {t(auditChanged ? copy.auditChanged : copy.auditUnchanged)}
              </StatusDot>
              <span className="rd-num" style={{ fontSize: 13, color: "var(--rd-text-tertiary)" }}>
                {t(copy.httpStatus, { status: step.acceptance.httpStatus })}
              </span>
            </div>
            <div
              style={{
                maxWidth: "100%",
                overflowX: "auto",
                borderRadius: "var(--rd-radius-card)",
                boxShadow: "var(--rd-shadow-card)",
              }}
            >
              <table style={{ width: "100%", minWidth: 340, borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th scope="col" style={th}>
                      {t(copy.field)}
                    </th>
                    <th scope="col" style={th}>
                      {t(copy.before)}
                    </th>
                    <th scope="col" style={th}>
                      {t(copy.after)}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.field}>
                      <th scope="row" style={{ ...td, textAlign: "start", fontWeight: 500 }}>
                        {t(
                          (copy.fields as Record<string, string>)[
                            row.field.replace(/\./g, "_")
                          ] ?? copy.unknownCode,
                          { code: row.field },
                        )}
                      </th>
                      <td style={td} dir="auto">
                        {cell(row.before)}
                      </td>
                      <td style={td} dir="auto">
                        {cell(row.after)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <CheckList checks={step.checks} />
            <Technical summary={t(copy.responseDetails)}>{sanitizedJson(step.acceptance)}</Technical>
          </SimCard>
        );
      })}
      {result.checks.length ? (
        <SimCard title={t(copy.checks)}>
          <CheckList checks={result.checks} />
        </SimCard>
      ) : null}
      <Technical summary={t(copy.serverDetails)}>
        {sanitizedJson({
          limits: result.limits,
          checks: result.checks,
          stepChecks: result.steps.map((step) => ({
            sequence: step.sequence,
            checks: step.checks,
          })),
        })}
      </Technical>
    </div>
  );
}
